import React, { useContext, useMemo } from 'react'
import { ConfigurationContext } from '../../../../components/campaign-builder/configuration-editor/configuration-context'
import { getTriggerFlags } from '../../../../utils/trigger-flags'
import { extractCampaignTrigger } from '../../../../_utils/campaigns'
import { JourneyType } from '@pushly/models/lib/enums/journeys'
import { Well } from '@pushly/aqe/lib/components'
import { Skeleton } from 'antd'
import { FEAT_ABANDONED_VIEW_CAMPAIGNS, FEAT_SAVED_ITEM_CAMPAIGNS } from '../../../../constants'
import { CatalogItemType } from '@pushly/models/lib/enums'
import AbandonmentSettingsSection from './abandonment-settings-section'
import SubscriberEntryCapSection from './subscriber-entry-cap-section'
import { TriggerSection } from './trigger-section'
import { RecurrenceSection } from './recurrence-section'
import { useTriggerHandlers } from '../../hooks/entry/use-trigger-handlers'
import { CampaignEditableState } from '../../../../components/campaign-builder/enums'
import {
    getDefaultCatalogItemType,
    getDefaultMaxAbandonedItemAgeDays,
    getItemTypeFromTrigger,
} from '../../utils/defaults'
import { NextOccurrencesFromSchedule } from '../../../../components/scheduling'

// Keep prop type explicit for future extensibility
export type JourneyEntryWellProps = {}

export const JourneyEntryWell: React.FC<JourneyEntryWellProps> = () => {
    const { campaign, mode, loading, domain, domainDefaultSegment, editableState, setCampaign, setFlags } =
        useContext(ConfigurationContext)

    if (!campaign) return null

    // Determine journey and trigger info using existing helpers for parity
    const journeyType = campaign.getType?.()
    const triggerType = (() => {
        try {
            const trigger = extractCampaignTrigger(campaign)
            const trigCfg = trigger?.getConfiguration?.()
            return trigCfg?.type
        } catch {
            return undefined
        }
    })()

    const flags = useMemo(() => getTriggerFlags(triggerType), [triggerType])

    const computeCatalogItemType = useMemo<CatalogItemType>(() => {
        const fromTrigger = getItemTypeFromTrigger(campaign)
        if (fromTrigger) return fromTrigger

        return getDefaultCatalogItemType(domain)
    }, [campaign, domain?.ecommConfig?.itemType])

    const computedDefaultCartItemAgeDaysMax = useMemo<number>(
        () => getDefaultMaxAbandonedItemAgeDays(domain),
        [domain?.ecommConfig?.maxAbandonedItemAgeDays],
    )

    const { handleTriggerTypeChange } = useTriggerHandlers({
        campaign,
        catalogItemType: computeCatalogItemType,
        defaultMaxAgeDays: computedDefaultCartItemAgeDaysMax,
        domainDefaultSegment,
        setCampaign,
        setFlags,
    })

    // Abandonment-related models
    const currConfig = campaign.getConfiguration()
    const currTrigger = extractCampaignTrigger(campaign)

    const entryCap = currConfig.getSubscriberFrequencyCap?.()

    return (
        <Well
            className={`campaign-entry-well nested mode-${mode} ${journeyType === JourneyType.TRIGGER ? 'triggered' : ''} ${journeyType === JourneyType.RECURRING ? 'recurring' : ''}`}
            title="Entry"
            hideFooter={true}
        >
            <Skeleton loading={loading} active={true} title={false} paragraph={{ rows: 1, width: '100%' }}>
                {journeyType === JourneyType.TRIGGER && (
                    <TriggerSection
                        trigger={currTrigger}
                        hasAbandonedViewAccess={(domain?.flags ?? []).includes(FEAT_ABANDONED_VIEW_CAMPAIGNS)}
                        hasSavedItemsAccess={(domain?.flags ?? []).includes(FEAT_SAVED_ITEM_CAMPAIGNS)}
                        onChange={handleTriggerTypeChange}
                        domainItemType={domain?.ecommConfig?.itemType}
                        disabled={!!campaign.getId()}
                    />
                )}

                {journeyType === JourneyType.RECURRING &&
                    (() => {
                        const schedule = campaign.getConfiguration().getSchedule?.()
                        return (
                            <div className="recurrence-schedule-wrapper">
                                {schedule && (
                                    <RecurrenceSection
                                        schedule={schedule}
                                        editable={editableState !== CampaignEditableState.COMPLETED}
                                        domain={domain}
                                        campaignTimeZone={campaign.getDateTimeZone?.() || 'UTC'}
                                        onUpdateSchedule={(mutate) => {
                                            const cloned = campaign.clone()
                                            const sch = cloned.getConfiguration().getSchedule()
                                            if (sch) {
                                                mutate(sch)
                                            }
                                            setCampaign(cloned)
                                        }}
                                    />
                                )}
                                {(() => {
                                    const schPreview = campaign?.getConfiguration?.()?.getSchedule?.()
                                    const scheduleLike = {
                                        serialize: () => schPreview?.serialize?.(),
                                        getTimeZone: () => schPreview?.getTimeZone?.(),
                                        getDateStart: () => campaign?.getDateStart?.(),
                                        getDateEnd: () => campaign?.getDateEnd?.(),
                                    }
                                    return <NextOccurrencesFromSchedule scheduleLike={scheduleLike} />
                                })()}
                            </div>
                        )
                    })()}

                <AbandonmentSettingsSection
                    showAbandonment={flags.isAbandonment}
                    showCooldown={flags.isAbandonedView}
                    showMinimumItemViews={flags.isAbandonedView}
                    showMaxItemAge={flags.isAbandonedCart || flags.isSavedItem}
                    itemType={computeCatalogItemType}
                    triggerType={triggerType}
                    configuration={currConfig}
                    triggerConfig={currTrigger}
                    defaultMaxItemAgeDays={computedDefaultCartItemAgeDaysMax}
                    onApply={(apply) => {
                        const cloned = campaign.clone()
                        const cfg = cloned.getConfiguration()
                        const trig = extractCampaignTrigger(cloned)
                        apply(cfg, trig)
                        setCampaign(cloned)
                    }}
                />

                {!flags.isSubscribed && (
                    <SubscriberEntryCapSection
                        disabled={editableState === CampaignEditableState.COMPLETED}
                        cap={entryCap}
                        onChange={(next) => {
                            const cloned = campaign.clone()
                            const cfg = cloned.getConfiguration()
                            cfg.setSubscriberFrequencyCap?.(next)
                            setCampaign(cloned)
                        }}
                    />
                )}
            </Skeleton>
        </Well>
    )
}

export default JourneyEntryWell
